import { prop, groupBy, pipe, sum, pluck, map, curry, pick } from 'ramda'
import {
  formatDateToString,
  getCorrespondingPreviousPeriodDate,
  getCorrespondingPreviousYearDate,
  format
} from 'shared/dateFns'
import { type TimePeriodType } from '../components/utils'
import { formDataKey } from '../components/utils'
import { type ProgressOverTimeRecords } from 'src/home/components/ProgressOvertime'
import { type ReportingBaseReport } from './reportingBase'
import { type ChannelGroupingPerformanceReport } from './channelGrouping'
import { type OriginalRow } from 'shared/reactTable/types'
import { type Metric } from 'src/home/reportTemplateParameters'
import { getBudgetFieldFromRelatedMetric } from 'src/home/budgetsAndTargets'
import { CALCULATED_METRICS_MAP } from 'src/home/constants'

export type Report = ReportingBaseReport | ChannelGroupingPerformanceReport

const groupByChannel = groupBy<{ channel: string }>(prop('channel'))

const groupByMarket = groupBy<{ market: string }>(prop('market'))

// @ts-expect-error - This is a valid key, but TS doesn't know that
export const total = curry((reports: Report[], field: string): number => pipe(pluck(field), sum)(reports) as number)

type FilterIds = 'markets' | 'channels'

export type Filters = Record<
  FilterIds,
  Set<{
    id: string
    name: string
  }>
>

export const transformToFilters = <
  T extends {
    channel: string
    market: string
  }
>(
  records: T[]
): Filters => {
  return {
    markets: new Set(Object.keys(groupByMarket(records)).map((market: string) => ({ name: market, id: market }))),

    channels: new Set(Object.keys(groupByChannel(records)).map((channel: string) => ({ name: channel, id: channel })))
  }
}

export type CalculateTotalsFn<TReport extends Report, TMetric extends Metric> = ({
  records,
  metrics,
  market,
  channel
}: {
  records: TReport[]
  metrics: TMetric[]
  market?: string
  channel?: string
}) => Record<string, number>

export type TransformToTimeSeriesByMetricAndBreakdownFn<TReport extends Report, TMetric extends Metric> = ({
  records,
  previousRecords,
  metrics,
  channels,
  markets,
  daysRange,
  isComparisonMode,
  selectedPeriodType,
  isBudgetMode,
  isCumulativeMode
}: {
  records: TReport[]
  previousRecords: TReport[]
  metrics: TMetric[]
  channels: string[]
  markets: string[]
  daysRange: number
  isComparisonMode: boolean
  selectedPeriodType: TimePeriodType
  isBudgetMode: boolean
  isCumulativeMode: boolean
}) => ProgressOverTimeRecords

export type TransformToDetailedDataFn<TReport extends Report> = ({
  reports,
  reportsForPreviousTimePeriod,
  selectedDimensions
}: {
  reports: TReport[]
  reportsForPreviousTimePeriod: TReport[]
  selectedDimensions: Array<'market' | 'channel' | 'date'>
}) => OriginalRow[]

export const transformToTimeSeriesByMetricAndBreakdown = <
  TReport extends ReportingBaseReport | ChannelGroupingPerformanceReport,
  TMetric extends Metric
>({
  records,
  previousRecords,
  metrics,
  channels,
  markets,
  daysRange,
  isComparisonMode,
  selectedPeriodType,
  calculateTotals,
  isBudgetMode,
  isCumulativeMode
}: {
  records: TReport[]
  previousRecords: TReport[]
  metrics: TMetric[]
  channels: string[]
  markets: string[]
  daysRange: number
  isComparisonMode: boolean
  selectedPeriodType: TimePeriodType
  calculateTotals: CalculateTotalsFn<TReport, TMetric>
  // TODO: leaking the idea of a budget mode, should be refactored so that you can pass, a list of metrics to show and also a list of metrics to not do previous date comparison
  // then you dont need to pass the isBudgetMode flag, could add a showPrevious period array and then if comparison mode is selected can specify which metrics to show previous period for
  isBudgetMode: boolean
  isCumulativeMode: boolean
}): ProgressOverTimeRecords => {
  const budgetMetricsSet = new Set<TMetric>()

  if (isComparisonMode && isBudgetMode) {
    metrics.forEach((metric) => {
      const budgetField = getBudgetFieldFromRelatedMetric(metric)
      // check that field is not already in the list
      if (budgetField != null) {
        budgetMetricsSet.add(budgetField as TMetric)
      }
    })
  }

  const budgetMetricsArray = Array.from(budgetMetricsSet)

  // base don the selected metrics and the budget metrics we will also pre compute the dependent metrics
  // if they are calculated metrics.
  //   e.g. if roas is in the list of metrics then we will also calculate intermediate summaries for spend and conversionValue
  //  if budgetDepletion is in the list of metrics then we will also calculate intermediate summaries for spend and dailyBudgetxs

  const dependentMetricsSet = new Set<TMetric>()

  if (isCumulativeMode) {
    // TODO: fix a lot of type coercion here
    metrics.forEach((metric) => {
      const metricMetadata = CALCULATED_METRICS_MAP[metric]
      if (metricMetadata != null) {
        const dependentMetrics = metricMetadata.dependentMetrics
        dependentMetrics.forEach((dependentMetric) => {
          dependentMetricsSet.add(dependentMetric as TMetric)
        })
      }
    })
  }

  const dependentMetricsArray = Array.from(dependentMetricsSet)

  const unionMetrics = [...metrics, ...budgetMetricsArray, ...dependentMetricsArray]
  const visibleMetrics = [...metrics, ...budgetMetricsArray]
  const breakdownCount = channels.length + markets.length
  const hasBreakdown = breakdownCount > 0

  const filterByBreakdown = (record: Report): boolean => {
    return !hasBreakdown || channels.includes(record.channel) || markets.includes(record.market)
  }

  const filteredRecords = records.filter(filterByBreakdown)
  const previousFilteredRecords = breakdownCount > 2 ? [] : previousRecords.filter(filterByBreakdown)

  filteredRecords.sort(
    (firstRecord, secondRecord) =>
      new Date(firstRecord.createdAt).getTime() - new Date(secondRecord.createdAt).getTime()
  )

  const reportsGroupedByDate = groupBy<TReport>((record) => record.createdAt, filteredRecords)

  const previousReportsGroupedByDate = groupBy<TReport>(
    (record) => format(new Date(record.createdAt), 'yyyy-MM-dd'),
    previousFilteredRecords
  )

  const totalOnReportTypeByDate: Record<string, Record<string, number>> = {}

  const previousTotalOnReportTypeByDate: Record<string, Record<string, number>> = {}

  const dates = Object.keys(reportsGroupedByDate)

  // For 'custom' the comparison range is an arbitrary period of any length, so day-over-day
  // alignment can't be derived by shifting; instead align by ordinal position — the i-th day
  // of the selected range maps to the i-th day of the custom comparison range.
  const sortedPreviousDates = Object.keys(previousReportsGroupedByDate).sort((a, b) => a.localeCompare(b))
  const customComparisonDateBySelectedDate: Record<string, string> = {}
  dates.forEach((date, index) => {
    customComparisonDateBySelectedDate[date] = sortedPreviousDates[index]
  })

  const getComparisonDate = (date: string): string => {
    if (selectedPeriodType === 'custom') {
      return customComparisonDateBySelectedDate[date]
    }
    return selectedPeriodType === 'previous_year'
      ? getCorrespondingPreviousYearDate(date)
      : getCorrespondingPreviousPeriodDate(date, daysRange)
  }

  dates.forEach((date) => {
    const records = reportsGroupedByDate[date]
    const previousDate = getComparisonDate(date)

    totalOnReportTypeByDate[date] = {
      ...(!hasBreakdown && { ...calculateTotals({ records, metrics: unionMetrics }) }),
      ...markets.reduce(
        (acc, market) => ({ ...acc, ...calculateTotals({ records, metrics: unionMetrics, market }) }),
        {}
      ),
      ...channels.reduce(
        (acc, channel) => ({ ...acc, ...calculateTotals({ records, metrics: unionMetrics, channel }) }),
        {}
      )
    }

    if (isComparisonMode) {
      previousTotalOnReportTypeByDate[previousDate] = {
        ...(!hasBreakdown && {
          ...calculateTotals({ records: previousReportsGroupedByDate[previousDate] ?? [], metrics: unionMetrics })
        }),
        ...markets.reduce(
          (acc, market) => ({
            ...acc,
            ...calculateTotals({
              records: previousReportsGroupedByDate[previousDate] ?? [],
              metrics: unionMetrics,
              market
            })
          }),
          {}
        ),
        ...channels.reduce(
          (acc, channel) => ({
            ...acc,
            ...calculateTotals({
              records: previousReportsGroupedByDate[previousDate] ?? [],
              metrics: unionMetrics,
              channel
            })
          }),
          {}
        )
      }
    }
  })

  const breakdowns = [...markets, ...channels]

  const dailyRecords = map((date) => {
    return {
      x: formatDateToString({ date, targetFormat: 'MMM do' }),
      fullDate: date,
      ...unionMetrics.reduce<Record<string, string | number>>((acc, metric) => {
        const isBudgetMetric = budgetMetricsArray.includes(metric)

        if (totalOnReportTypeByDate[date][metric] != null) {
          const dataKey = formDataKey({ metric })

          acc[dataKey] = totalOnReportTypeByDate[date][metric]

          // the comparison mode shouldnt show the prior period for budget metrics
          if (isComparisonMode && !isBudgetMetric) {
            const correspondingPreviousDate = getComparisonDate(date)

            const previousDataKey = formDataKey({ metric, isPreviousMetric: true })

            acc[previousDataKey] = previousTotalOnReportTypeByDate[correspondingPreviousDate]?.[metric] ?? 0
          }
        }

        breakdowns.forEach((breakdown) => {
          const breakdownDataKey = formDataKey({ metric, breakdown })

          if (totalOnReportTypeByDate[date][breakdownDataKey] == null) {
            return acc
          }
          acc[breakdownDataKey] = totalOnReportTypeByDate[date][breakdownDataKey]

          if (breakdowns.length <= 2 && isComparisonMode && !isBudgetMetric) {
            const correspondingPreviousDate = getComparisonDate(date)

            const previousBreakdownDataKey = formDataKey({ metric, breakdown, isPreviousMetric: true })
            acc[previousBreakdownDataKey] =
              previousTotalOnReportTypeByDate[correspondingPreviousDate]?.[breakdownDataKey] ?? 0
          }
        })
        return acc
      }, {})
    }
  }, dates)
  // if cumulative mode is selected, we need to calculate the cumulative totals
  const cumulativeRecords: ProgressOverTimeRecords = []
  if (isCumulativeMode) {
    dailyRecords.forEach((record, idx) => {
      if (idx === 0) {
        cumulativeRecords.push(record)
      } else {
        const newRecord: ProgressOverTimeRecords[number] = { ...record }
        Object.keys(newRecord).forEach((key) => {
          if (key !== 'x' && key !== 'fullDate') {
            newRecord[key] = (newRecord[key] as number) + (cumulativeRecords[idx - 1][key] as number)
          }
        })
        // loop over keys in newRecord and if the start with roas, replace them with the calcualtion based on spend and conversionValue
        Object.keys(newRecord).forEach((key) => {
          // Find the metric type by checking which metric prefix matches the key
          const metricType = Object.keys(CALCULATED_METRICS_MAP).find((metric) => key.startsWith(metric))
          if (metricType == null) {
            return
          }
          const metricMetadata = CALCULATED_METRICS_MAP[metricType as TMetric]
          if (metricMetadata == null) {
            return
          }

          // Get the suffix (if any) from the original key (e.g., "_segment" from "roas_segment")
          const suffix = key.slice(metricType.length)

          // Get the dependent metric values using the same suffix
          const dependentValues = metricMetadata.dependentMetrics.map((metric) => {
            const dependentKey = metric + suffix
            return newRecord[dependentKey] as number
          })

          // Calculate the new value using the calculation function
          // coerce the values to number
          newRecord[key] = metricMetadata.calculationFunction(...dependentValues) ?? 0
        })

        cumulativeRecords.push(newRecord)
      }
    })
  }

  const calculatedRecords = isCumulativeMode ? cumulativeRecords : dailyRecords

  // build a set of metrics that are in the dependent metrics set but not in the union metrics set
  const metricsToExclude = dependentMetricsArray.filter((metric) => !visibleMetrics.includes(metric))
  // filter records columns to only include visible metrics
  const outputRecords = calculatedRecords.map((record) => {
    // exclude metrics that start with the metric name that is only a dependent metric
    const keys = Object.keys(record).filter(
      (key) => !metricsToExclude.some((term) => key === term || key.startsWith(term + '_'))
    )
    // fixes the issue with Pick returning that the type is not the same as the original record and
    // potentially missing required fields
    return pick(keys, record) as ProgressOverTimeRecords[number]
  })
  return outputRecords
}
